/**
 * useCaptureSession — encapsulates the core capture session state machine
 * for ChildEntryForm, reducing the state management burden on the component.
 *
 * Manages:
 * - Form data state (CaptureFormData discriminated union)
 * - Receipt photos and AI analysis results
 * - Multi-currency detection
 * - Tax resolution (via extracted taxResolution.ts)
 * - Line item building (via extracted lineItemBuilder.ts)
 * - Metadata construction for submission
 * - Photo upload with retry logic
 * - Expense intake API submission
 *
 * This hook is designed to be used by ChildEntryForm and any future thin
 * renderers that need the same capture logic without the presentation layer.
 */

'use client';

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { authService } from '@/lib/authService';
import notify from '@/lib/notifications';
import {
  type ReceiptAiPayload,
  type ReceiptAiValidation,
  buildReceiptAmountValidation,
} from '@/lib/receiptAiAnalysis';
import { resolveExpenseAccountMappingCode } from '@/lib/finance/expenseAccountMapping';
import { inferActionType } from '@/lib/finance/expenseIntakeUtils';
import { evaluateCapturePolicy } from './capturePolicy';
import { isVehicleLinkedExpenseType } from '@/app/client-zone/modules/total-operations/utils/expenseLinkage';
import type { ChildEntryType, GPSLocation, FeatureFlags } from '../../types';
import {
  EXPENSE_ENTRY_TYPES,
  ENTRY_CONFIG,
  getEntryTypeConfig,
} from './constants';
import type { AiCategoryDisagreement } from './useReceiptAnalysis';
import { detectVatTreatment, detectMixedVatSplit, correctReceiptTaxAmount } from './taxResolution';
import { extractDateFromRawText } from '@/lib/finance/dinNormalizer';
import { supplierNamesMatch } from '@/lib/finance/supplierNameMatch';
import { buildBomLineItems, toIntakeLineItems, type BomLineItem } from './lineItemBuilder';
import {
  type CaptureFormData,
  type CaptureFieldKey,
  createInitialFormData,
  toRecordString,
  fromRecordString,
} from './captureSchema';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('useCaptureSession');

type ChildEntryMetadata = Record<string, unknown>;

export interface ChildEntryFormData {
  type: ChildEntryType;
  work_item_id: string;
  timestamp: string;
  location?: GPSLocation;
  amount?: number;
  notes?: string;
  photos?: string[];
  metadata: ChildEntryMetadata;
  already_persisted?: boolean;
}

export interface CaptureSessionSubmitInput {
  selectedEntryType: ChildEntryType | null;
  workItemId: string;
  linkToWorkItem: boolean;
  linkableWorkItemId?: string | null;
  selectedJob?: { id: string; item_number?: string; title?: string } | null;
  selectedCustomer?: { id: number; company_name?: string | null } | null;
  linkingMode?: 'job' | 'supplier' | 'both' | 'general' | 'vehicle' | 'wages' | null;
  selectedSupplier?: { id: number; company_name?: string | null; name?: string } | null;
  selectedPurchaseOrder?: { id: string; item_number: string } | null;
  linkToSupplier?: boolean;
  linkToVehicle?: boolean;
  vehicleId?: string | null;
  vehicleRegistration?: string | null;
  gpsLocation?: GPSLocation | null;
  workflowMode?: string | null;
  requiresFinanceApproval?: boolean;
  receiptCategorySeverity?: 'blocking' | 'advisory' | null;
  /** AI-vs-technician categorization disagreement. Set when the
   *  categorization was locked (a linking mode or job-linked expense was
   *  selected) but the AI detected a different expense type. The technician's
   *  selection is kept — this is persisted as a review flag on the DIN. */
  aiCategoryDisagreement?: AiCategoryDisagreement | null;
  needsOdometer?: boolean | null;
  odometerResult?: {
    reading: number;
    source: string;
    tracked_provider?: string | null;
    verification?: {
      confidence?: number | null;
      validation?: string | null;
      detected_reading?: number | null;
    } | null;
    photo?: string | null;
  } | null;
  isCasualWage?: boolean | null;
  isProcurementType?: boolean;
  requiresPurchaseOrder?: boolean;
  poFulfillment?: { totalOrdered: number; totalReceived: number } | null;
  poLineItems?: Array<{ quantity?: number }>;
  /** True when the technician acknowledged a supplier name mismatch
   *  (AI vendor name ≠ selected supplier). Stored as a flag for admin review. */
  supplierMismatchAcknowledged?: boolean;
  /** The AI-detected vendor name (from OCR). Stored alongside the selected
   *  supplier so admins can see the mismatch in DocumentModal. */
  aiVendorName?: string | null;
  /** True when the technician explicitly reverted the payment-terms
   *  auto-switch — tells intake to keep the cash 'expense' subtype even
   *  though the selected supplier has payment terms. */
  paymentTermsReverted?: boolean;
  expenseLineItems: Array<{
    description: string;
    quantity: number;
    unit_price: number;
    line_total?: number | null;
    tax_rate?: number | null;
    tax_amount?: number | null;
    unit?: string;
  }>;
  policies: unknown;
  expenseAccounts: Array<{ account_code: string; name: string }>;
  expenseAccountMappings: Record<string, unknown>;
  clientId: string | null;
  onSubmit: (data: ChildEntryFormData) => void;
}

export interface UseCaptureSessionReturn {
  formData: CaptureFormData;
  setFormData: React.Dispatch<React.SetStateAction<CaptureFormData>>;
  setFormField: (key: CaptureFieldKey, value: string) => void;
  resetForm: () => void;
  /** Clear the idempotency session key so the next submit gets a fresh DIN. */
  resetSessionId: () => void;
  /** Legacy accessor for components still expecting Record<string, string> */
  formDataRecord: Record<string, string>;

  receiptPhotos: string[];
  setReceiptPhotos: React.Dispatch<React.SetStateAction<string[]>>;
  receiptResult: ReceiptAiPayload | null;
  setReceiptResult: React.Dispatch<React.SetStateAction<ReceiptAiPayload | null>>;
  receiptValidation: ReceiptAiValidation | null;
  setReceiptValidation: React.Dispatch<React.SetStateAction<ReceiptAiValidation | null>>;

  detectedCurrency: string;
  setDetectedCurrency: React.Dispatch<React.SetStateAction<string>>;
  baseCurrency: string;

  submitting: boolean;
  loadingPhase: 'analyzing' | 'compressing' | 'saving' | null;
  submit: (input: CaptureSessionSubmitInput) => Promise<void>;
  canSubmit: boolean;
}

export function useCaptureSession(isOpen: boolean, publicUploadToken?: string | null): UseCaptureSessionReturn {
  const [formData, setFormData] = useState<CaptureFormData>({ type: 'purchase' });
  const [submitting, setSubmitting] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState<'analyzing' | 'compressing' | 'saving' | null>(null);
  const submittingRef = useRef(false);

  // Stable idempotency key per form session — survives retries so the backend
  // can deduplicate if the first request succeeded but the response was lost.
  const sessionIdRef = useRef<string>('');

  const [receiptPhotos, setReceiptPhotos] = useState<string[]>([]);
  const [receiptResult, setReceiptResult] = useState<ReceiptAiPayload | null>(null);
  const [receiptValidation, setReceiptValidation] = useState<ReceiptAiValidation | null>(null);

  const [detectedCurrency, setDetectedCurrency] = useState<string>('ZAR');
  const [baseCurrency, setBaseCurrency] = useState<string>('ZAR');

  // Fetch tenant base currency on mount
  useEffect(() => {
    if (!isOpen) return;
    const token = authService.getTokens()?.access_token;
    if (!token) return;
    fetch('/api/v1/finance/multi-currency?action=base_currency', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (json?.success && json?.data?.base_currency) {
          setBaseCurrency(json.data.base_currency);
          setDetectedCurrency(json.data.base_currency);
        }
      })
      .catch(() => undefined);
  }, [isOpen]);

  // Update detected currency when AI receipt result changes
  // Only override the base currency if the OCR result is different AND there's
  // strong evidence (e.g. explicit foreign currency). This prevents false positives
  // like a $ in a product description being misinterpreted as USD on a ZAR receipt.
  useEffect(() => {
    if (receiptResult?.currency && /^[A-Z]{3}$/.test(receiptResult.currency)) {
      const ocrCurrency = receiptResult.currency;
      if (ocrCurrency !== baseCurrency) {
        // Sanity check: look for strong foreign currency indicators in raw text
        const rawText = String(receiptResult.raw_text || '').toUpperCase();
        const hasStrongForeignIndicator =
          /\bUSD\b|\bDOLLAR\b|\bEUR\b|\bEURO\b|\bGBP\b|\bPOUND\b|\b£\b/.test(rawText);
        if (hasStrongForeignIndicator) {
          setDetectedCurrency(ocrCurrency);
        }
        // Otherwise keep baseCurrency — the OCR currency was likely a false positive
      } else {
        setDetectedCurrency(ocrCurrency);
      }
    }
  }, [receiptResult, baseCurrency]);

  const setFormField = useCallback((key: CaptureFieldKey, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }) as CaptureFormData);
  }, []);

  const formDataRecord = useMemo(() => toRecordString(formData), [formData]);

  const resetForm = useCallback(() => {
    setFormData({ type: 'purchase' });
    setReceiptPhotos([]);
    setReceiptResult(null);
    setReceiptValidation(null);
    setDetectedCurrency(baseCurrency);
    submittingRef.current = false;
    setSubmitting(false);
    setLoadingPhase(null);
    sessionIdRef.current = '';
  }, [baseCurrency]);

  // Clear only the idempotency session key so the next submission gets a fresh
  // DIN number. Called by "Capture another" in ChildEntryForm — unlike
  // resetForm(), this preserves the current entry type and form context.
  const resetSessionId = useCallback(() => {
    sessionIdRef.current = '';
  }, []);

  const submit = useCallback(async (input: CaptureSessionSubmitInput) => {
    if (submittingRef.current) return;

    if (input.receiptCategorySeverity === 'blocking') {
      notify.error('Receipt category mismatch. Please capture under the correct expense type.');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    try {
      const preparedPhotos = receiptPhotos.length > 0 ? [...receiptPhotos] : [];
      const selectedEntryType = input.selectedEntryType;

      if (!selectedEntryType) {
        notify.error('No entry type selected.');
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      // ── Public mode: submit through the token-authenticated public endpoint ──
      // Skips all auth-dependent features (policy validation, expense accounts,
      // procurement, vehicle linkage, vendor memory). The public endpoint does
      // its own AI extraction + intake pipeline; we PATCH the user's form
      // corrections afterwards so the work item reflects the verified details.
      if (publicUploadToken) {
        if (preparedPhotos.length === 0) {
          notify.error('A receipt photo is required. Please capture or upload a photo before submitting.');
          setSubmitting(false);
          submittingRef.current = false;
          return;
        }

        setLoadingPhase('saving');

        try {
          // Convert the first photo (base64 data URI) to a File for FormData upload
          const firstPhoto = preparedPhotos[0];
          const match = /^data:(image\/[a-zA-Z]+);base64,(.*)$/.exec(firstPhoto);
          const mime = match?.[1] || 'image/jpeg';
          const raw = match?.[2] || firstPhoto;
          const byteString = atob(raw);
          const bytes = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
          const blob = new Blob([bytes], { type: mime });
          const ext = mime === 'image/png' ? 'png' : 'jpg';
          const file = new File([blob], `capture-${Date.now()}.${ext}`, { type: mime });

          // POST the file to the public endpoint (server does AI extraction + intake)
          const formDataObj = new FormData();
          formDataObj.append('file', file);

          const postRes = await fetch(`/api/v1/public/document-upload/${encodeURIComponent(publicUploadToken)}`, {
            method: 'POST',
            body: formDataObj,
          });

          const postResult = await postRes.json().catch(() => null);
          if (!postRes.ok || !postResult?.success) {
            const rawErr = postResult?.error || `Upload failed (HTTP ${postRes.status})`;
            notify.error(`Failed to submit document: ${rawErr}`);
            setSubmitting(false);
            submittingRef.current = false;
            return;
          }

          const intakeLogId = postResult.data?.intake_log_id as string | undefined;
          const itemNumber = postResult.data?.din_number as string | undefined;

          // PATCH the user's form corrections so the work item reflects verified details
          if (intakeLogId) {
            try {
              const amount = formDataRecord.amount ? Number(formDataRecord.amount) : undefined;
              const vendorName = formDataRecord.vendor || formDataRecord.supplier_name || receiptResult?.vendor_name || undefined;
              const expenseDate = formDataRecord.date || extractDateFromRawText(receiptResult?.raw_text) || new Date().toISOString().split('T')[0];
              const receiptNumber = formDataRecord.receipt_number || receiptResult?.receipt_number || undefined;
              const notes = formDataRecord.notes || formDataRecord.description || undefined;

              await fetch(`/api/v1/public/document-upload/${encodeURIComponent(publicUploadToken)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  intake_log_id: intakeLogId,
                  duplicate_acknowledged: false,
                  edited_payload: {
                    vendor_name: vendorName || null,
                    receipt_date: expenseDate,
                    receipt_number: receiptNumber || null,
                    total_amount: amount ?? null,
                    currency: detectedCurrency,
                    notes: notes || undefined,
                  },
                }),
              });
            } catch (patchErr) {
              if (!isSilentFetchError(patchErr)) logger.warn('[useCaptureSession] Public PATCH corrections failed (non-fatal):', patchErr);
            }
          }

          // No success toast here — the submission receipt modal already
          // confirms the submission and shows the DIN.
          input.onSubmit({
            type: selectedEntryType,
            work_item_id: intakeLogId || '',
            timestamp: new Date().toISOString(),
            amount: formDataRecord.amount ? Number(formDataRecord.amount) : undefined,
            notes: formDataRecord.notes || formDataRecord.description || undefined,
            photos: preparedPhotos,
            metadata: {
              expense_intake_id: intakeLogId,
              item_number: itemNumber,
              source: 'public_upload_link',
            },
            already_persisted: true,
          });
        } catch (publicErr) {
          if (!isSilentFetchError(publicErr)) logger.error('[useCaptureSession] Public submit error:', publicErr);
          notify.error('Failed to submit document. Please try again.');
        } finally {
          setSubmitting(false);
          setLoadingPhase(null);
          submittingRef.current = false;
        }
        return;
      }

      // ── Validation gates ──
      if (input.isCasualWage && preparedPhotos.length === 0) {
        notify.error('Proof of authorization is required. Please attach a photo of the signed permission or WhatsApp authorization.');
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      const odometerValue = (formData as unknown as Record<string, string | undefined>).odometer;
      if (input.needsOdometer && (!odometerValue || String(odometerValue).trim() === '')) {
        notify.error('Odometer reading is required for fuel and maintenance expenses. Please capture the odometer reading.');
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      // Fuel expenses require litres — if the AI couldn't extract it from the
      // receipt, the user must enter it manually before saving.
      if (selectedEntryType === 'fuel') {
        const litresValue = (formData as unknown as Record<string, string | undefined>).litres;
        const litresNum = litresValue ? parseFloat(litresValue) : 0;
        if (!litresValue || String(litresValue).trim() === '' || isNaN(litresNum) || litresNum <= 0) {
          notify.error('Litres is required for fuel expenses. Please enter the number of litres from the fuel slip.');
          setSubmitting(false);
          submittingRef.current = false;
          return;
        }
      }

      // Vehicle-linked expenses must have a vehicle_id
      if (isVehicleLinkedExpenseType(selectedEntryType) && (!input.linkToVehicle || !input.vehicleId)) {
        notify.error('A vehicle is required for this expense type. Please link a vehicle before submitting.');
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      if (input.isCasualWage && !(input.linkToWorkItem && input.linkableWorkItemId) && !input.selectedJob) {
        notify.error('A job or appointment is required for casual wages. Please select one.');
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      if (input.isProcurementType && !input.linkToWorkItem && !input.selectedSupplier) {
        notify.error('A supplier is required. Please select a supplier before submitting.');
        setSubmitting(false);
        submittingRef.current = false;
        return;
      }

      // PO requirement removed — purchase orders are assigned by admin in
      // SupplierStatementsWorkspace.tsx, not at capture time. The technician
      // only needs to select the supplier; PO linking happens in back-office.

      setLoadingPhase('saving');

      const amount = formDataRecord.amount ? Number(formDataRecord.amount) : undefined;
      const notes = formDataRecord.notes || formDataRecord.description || undefined;
      const config = ENTRY_CONFIG[selectedEntryType as ChildEntryType];

      // ── Build metadata ──
      const metadata: ChildEntryMetadata = {};
      config.fields.forEach((f: { key: string }) => {
        if (f.key !== 'amount' && f.key !== 'notes' && formDataRecord[f.key]) {
          metadata[f.key] = formDataRecord[f.key];
        }
      });

      // Normalize reimbursement_route to lowercase snake_case for backend compatibility
      if (metadata.reimbursement_route) {
        const routeMap: Record<string, string> = {
          'payroll': 'payroll',
          'bank transfer': 'bank_transfer',
          'petty cash': 'petty_cash',
        };
        const routeStr = String(metadata.reimbursement_route);
        const normalized = routeMap[routeStr.toLowerCase()] || routeStr.toLowerCase().replace(/\s+/g, '_');
        metadata.reimbursement_route = normalized;
      }

      // Receipt AI validation metadata
      const derivedReceiptValidation = receiptResult
        ? buildReceiptAmountValidation(amount, receiptResult.total_amount)
        : null;

      if (receiptResult) {
        metadata.receipt_ai_validation = {
          vendor_name: receiptResult.vendor_name,
          vendor_address: receiptResult.vendor_address,
          receipt_number: receiptResult.receipt_number,
          receipt_date: receiptResult.receipt_date || extractDateFromRawText(receiptResult.raw_text),
          detected_amount: receiptResult.total_amount,
          subtotal: receiptResult.subtotal,
          tax_amount: receiptResult.tax_amount,
          currency: receiptResult.currency,
          payment_method: receiptResult.payment_method,
          suggested_payment_method: receiptResult.suggested_payment_method,
          suggested_category: receiptResult.suggested_category,
          suggested_description: receiptResult.suggested_description,
          document_type: receiptResult.document_type,
          layout_hint: receiptResult.layout_hint,
          page_count: receiptResult.page_count,
          capture_profile: 'auto',
          line_items: Array.isArray(receiptResult.line_items)
            ? receiptResult.line_items.map((item) => ({
              description: item.description,
              quantity: item.quantity,
              unit_price: item.unit_price,
              total: item.total,
            }))
            : [],
          raw_text: receiptResult.raw_text,
          confidence: receiptResult.confidence,
          validation_match: derivedReceiptValidation?.match || 'unknown',
          difference: derivedReceiptValidation?.difference,
          discrepancy_notes: receiptResult.discrepancy_notes,
        };
      }

      // Odometer metadata
      if (input.needsOdometer && input.odometerResult) {
        metadata.odometer_capture = {
          reading: input.odometerResult.reading,
          source: input.odometerResult.source,
          tracked_provider: input.odometerResult.tracked_provider || null,
          ai_confidence: input.odometerResult.verification?.confidence ?? null,
          ai_validation: input.odometerResult.verification?.validation ?? null,
          detected_reading: input.odometerResult.verification?.detected_reading ?? null,
          photo_data: input.odometerResult.photo || null,
          pending_verification: input.odometerResult.verification?.validation === 'pending_offline'
            || input.odometerResult.verification?.validation === 'api_error_fallback',
        };
        metadata.odometer = String(input.odometerResult.reading);
      }

      // Supplier & PO metadata
      if (input.isProcurementType) {
        if (input.selectedSupplier) {
          metadata.supplier_id = input.selectedSupplier.id;
          metadata.supplier_name = input.selectedSupplier.company_name || input.selectedSupplier.name;
        }
        if (input.selectedPurchaseOrder) {
          metadata.purchase_order_id = input.selectedPurchaseOrder.id;
          metadata.purchase_order_number = input.selectedPurchaseOrder.item_number;
        }
        if (input.poFulfillment) {
          metadata.po_total_ordered = input.poFulfillment.totalOrdered;
          metadata.po_total_received = input.poFulfillment.totalReceived;
        }
        // Supplier mismatch flag for procurement types (admin must acknowledge in DocumentModal)
        const aiVendor = input.aiVendorName || receiptResult?.vendor_name || null;
        const selName = input.selectedSupplier?.company_name || input.selectedSupplier?.name || null;
        if (aiVendor && selName && !supplierNamesMatch(aiVendor, selName)) {
          metadata.supplier_variance = true;
          metadata.supplier_variance_detail = {
            slip_vendor: aiVendor,
            linked_supplier: selName,
            linked_supplier_id: input.selectedSupplier?.id ?? null,
            user_selected: true,
            acknowledged_by_technician: !!input.supplierMismatchAcknowledged,
          };
        }
      }

      // Store linking mode for all expense types so the inbox can display
      // the full capture context (job, supplier, vehicle, wages, general, both).
      if (input.linkingMode) {
        metadata.linking_mode = input.linkingMode;
      }

      // Categorization variance: the technician's selection was locked but the
      // AI detected a different expense type. The selection is never overridden
      // — this flag lets DocumentModal / ExpenseInbox surface the disagreement
      // for admin review (same pattern as supplier_variance).
      if (input.aiCategoryDisagreement) {
        const d = input.aiCategoryDisagreement;
        const modeLabels: Record<string, string> = {
          supplier: 'Supplier Document',
          job: 'Job Expense',
          both: 'Supplier & Job',
          general: 'General Expense',
          vehicle: 'Vehicle Expense',
          wages: 'Casual Wages',
        };
        const selectedLabel = input.linkingMode === 'vehicle'
          ? (d.selectedType ? getEntryTypeConfig(d.selectedType).label : 'Vehicle Expense')
          : input.linkingMode
            ? (modeLabels[input.linkingMode] ?? input.linkingMode)
            : (d.selectedType ? getEntryTypeConfig(d.selectedType).label : null);
        metadata.category_variance = true;
        metadata.category_variance_detail = {
          linking_mode: input.linkingMode ?? null,
          selected_entry_type: d.selectedType,
          selected_label: selectedLabel,
          ai_detected_type: d.detectedType,
          ai_detected_label: d.detectedLabel ?? (d.detectedType ? getEntryTypeConfig(d.detectedType).label : null),
          ai_suggested_category: d.suggestedCategory,
          ai_document_type: d.documentType,
          confidence: d.confidence,
          technician_selection_preserved: true,
        };
      }

      // Explicit technician revert of the payment-terms auto-switch — the
      // intake route honors this and keeps the cash 'expense' subtype.
      if (input.paymentTermsReverted) {
        metadata.payment_terms_reverted = true;
      }

      if (selectedEntryType === 'purchase' && input.linkToSupplier && input.selectedSupplier) {
        metadata.supplier_id = input.selectedSupplier.id;
        const selectedSupplierName = input.selectedSupplier.company_name || input.selectedSupplier.name;
        metadata.supplier_name = selectedSupplierName;

        // Detect variance between AI-extracted vendor name and user-selected supplier
        const aiVendorName = formDataRecord.vendor || formDataRecord.supplier_name || receiptResult?.vendor_name || null;
        if (aiVendorName && selectedSupplierName) {
          if (!supplierNamesMatch(aiVendorName, selectedSupplierName)) {
            metadata.supplier_variance = true;
            metadata.supplier_variance_detail = {
              slip_vendor: aiVendorName,
              linked_supplier: selectedSupplierName,
              linked_supplier_id: input.selectedSupplier.id,
              user_selected: true,
              acknowledged_by_technician: !!input.supplierMismatchAcknowledged,
            };
          }
        }
      }

      const photos = preparedPhotos.length > 0 ? preparedPhotos : undefined;

      // ── Resolve expense account mapping code ──
      let resolvedAccountCode: string | null = null;
      if (EXPENSE_ENTRY_TYPES.includes(selectedEntryType)) {
        const category = selectedEntryType === 'purchase'
          ? (formDataRecord.category || formDataRecord.purchase_type || 'other')
          : selectedEntryType;
        resolvedAccountCode = resolveExpenseAccountMappingCode(
          category,
          input.expenseAccountMappings,
          input.expenseAccounts,
        );
      }

      // ── Resolve VAT treatment (rate + inclusive/exclusive) ──
      const vatTreatment = detectVatTreatment({
        selectedEntryType,
        receiptResult,
        detectedCurrency,
        vendorName: receiptResult?.vendor_name,
        preferredDefaultTaxRate: null,
      });
      const resolvedDefaultTaxRate = vatTreatment.rate;

      // ── Detect mixed VAT split (Hubdoc-style) ──
      // If the receipt has mixed VAT treatment (some items vatable, some non-vatable),
      // compute the taxable/non-taxable subtotals for a two-line summary.
      const mixedVatSplit = detectMixedVatSplit(receiptResult, vatTreatment.rate, vatTreatment.taxInclusive);

      // ── Build BOM line items ──
      const bomLineItems = buildBomLineItems({
        selectedEntryType,
        expenseLineItems: input.expenseLineItems as never[],
        receiptResult,
        amount,
        notes,
        entryLabel: config.label,
        defaultTaxRate: resolvedDefaultTaxRate,
        mixedVatSplit,
        // Pass fuel-specific context so the fallback line item gets a
        // meaningful description instead of empty/"line item 1".
        fuelType: formDataRecord.fuel_type || receiptResult?.fuel_type || null,
        litres: formDataRecord.litres || receiptResult?.litres || null,
        station: formDataRecord.station || receiptResult?.vendor_name || null,
        vehicleRegistration: input.vehicleRegistration || null,
      });

      // ── Enrich line items with allocation data based on linking mode ──
      // When the user selected 'job' or 'both' in ChildEntryForm, the expense
      // is linked to a customer+job — set allocation_type='job' and attach
      // the linked_job_id and linked_customer_id to every line item.
      // When 'general' or 'supplier' was selected, default to 'overhead' so
      // the DIN completion gate does not require a job link.
      // Casual wages ('wages' mode) are also job-linked when a job is selected.
      const isJobLinked = input.linkingMode === 'job' || input.linkingMode === 'both'
        || (input.linkingMode === 'wages' && Boolean(input.selectedJob));
      const allocationType = isJobLinked ? 'job' : 'overhead';
      const linkedJobId = isJobLinked ? (input.selectedJob?.id || undefined) : undefined;
      const linkedCustomerId = isJobLinked ? (input.selectedCustomer ? String(input.selectedCustomer.id) : undefined) : undefined;
      const linkedCustomerName = isJobLinked ? (input.selectedCustomer?.company_name || undefined) : undefined;

      const enrichedBomLineItems = bomLineItems.map((item) => ({
        ...item,
        allocation_type: item.allocation_type || allocationType,
        linked_job_id: item.linked_job_id || linkedJobId,
        linked_customer_id: item.linked_customer_id || linkedCustomerId,
        linked_customer_name: item.linked_customer_name || linkedCustomerName,
        // A job-linked capture is billable by declaration (QBO parity): the
        // DIN arrives with customer+job preselected and Billable checked.
        is_billable: item.is_billable ?? isJobLinked,
      }));

      // ── Expense & procurement intake submission ──
      if (EXPENSE_ENTRY_TYPES.includes(selectedEntryType)) {
        const policyValidation = evaluateCapturePolicy({
          selectedEntryType,
          formData,
          amount,
          policies: input.policies,
          clientId: input.clientId,
          currency: detectedCurrency,
        });

        if (!policyValidation.canSubmit) {
          notify.error(`Policy violation: ${policyValidation.blockers.join(', ')}`);
          setSubmitting(false);
          submittingRef.current = false;
          return;
        }

        if (policyValidation.warnings.length > 0) {
          for (const w of policyValidation.warnings) {
            notify.warning(w);
          }
        }

        if (policyValidation.requiresApproval) {
          notify.info(`Expense requires Level ${policyValidation.approvalLevel} approval`);
        }

        const token = authService.getTokens()?.access_token;
        if (!token) {
          throw new Error('Authentication required');
        }

        const resolvedWorkItemId = input.linkToWorkItem && input.linkableWorkItemId
          ? input.linkableWorkItemId
          : input.selectedJob
            ? input.selectedJob.id
            : input.workItemId;

        // ── Photo upload ──
        // All expense link-to types use the exact same cloud_storage upload method:
        // photos are sent as raw base64 to the expense-intake API, which uploads
        // them to cloud storage via uploadRawPhotoToCloudStorage(). This ensures
        // consistent photo handling regardless of whether the expense is linked
        // to a job, appointment, supplier, or submitted as a general expense.
        // Photos already uploaded during the OCR analysis come back on the
        // receipt as cloud refs — submit them as `photos` and skip the
        // base64 re-upload entirely. Falls back to raw_photos whenever the
        // OCR-time upload didn't cover every image (partial upload failure,
        // photos added after analysis, or offline capture).
        const ocrUploaded = receiptResult?.uploaded_photos ?? [];
        const uploadedPhotos: Array<{
          file_path: string;
          file_name: string;
          file_size: number;
          file_type: string;
          mime_type: string;
          captured_at: string;
          cloud_file_id?: string;
        }> = ocrUploaded.map((p, i) => ({
          file_path: p.file_path,
          file_name: p.file_name,
          file_size: preparedPhotos[i] ? Math.round(preparedPhotos[i].length * 0.75) : 0,
          file_type: p.file_type,
          mime_type: p.mime_type,
          captured_at: p.captured_at,
          cloud_file_id: p.cloud_file_id,
        }));

        const rawPhotosForIntake: string[] =
          uploadedPhotos.length === preparedPhotos.length ? [] : [...preparedPhotos];

        // ── Build intake payload ──
        const intakeLineItems = toIntakeLineItems(enrichedBomLineItems, resolvedDefaultTaxRate);
        const isProcurementEntry = selectedEntryType === 'delivery_note' || selectedEntryType === 'supplier_invoice' || selectedEntryType === 'supplier_quote' || selectedEntryType === 'grn' || selectedEntryType === 'purchase_return' || selectedEntryType === 'supplier_credit';

        if (!sessionIdRef.current) {
          sessionIdRef.current = `${resolvedWorkItemId || 'standalone'}-${selectedEntryType}-${crypto.randomUUID()}`;
        }
        const idempotencyKey = sessionIdRef.current;

        const intakePayload: Record<string, unknown> = {
          action_type: inferActionType(selectedEntryType),
          idempotency_key: idempotencyKey,
          work_item_id: resolvedWorkItemId || undefined,
          vendor_name: formDataRecord.vendor || formDataRecord.supplier_name || null,
          amount: amount ?? 0,
          currency: detectedCurrency,
          expense_date: formDataRecord.date || extractDateFromRawText(receiptResult?.raw_text) || new Date().toISOString().split('T')[0],
          due_date: formDataRecord.due_date || undefined,
          notes: notes || '',
          receipt_url: uploadedPhotos.length > 0 ? uploadedPhotos[0].file_path : null,
          line_items: intakeLineItems,
          vat_amount: intakeLineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0),
          vat_rate: resolvedDefaultTaxRate,
          round_off: receiptResult?.round_off != null
            ? Number(receiptResult.round_off)
            : (receiptResult?.subtotal != null && receiptResult?.total_amount != null
              ? Math.round((Number(receiptResult.total_amount) - Number(receiptResult.subtotal) - (correctReceiptTaxAmount(
                  receiptResult.tax_amount != null ? Number(receiptResult.tax_amount) : null,
                  Number(receiptResult.subtotal),
                  Number(receiptResult.total_amount),
                  receiptResult.raw_text,
                ) ?? 0)) * 100) / 100
              : undefined),
          photos: uploadedPhotos,
          raw_photos: rawPhotosForIntake.length > 0 ? rawPhotosForIntake : undefined,
          custom_fields: {
            ...metadata,
            expense_account_code: resolvedAccountCode,
            source: 'smarttask',
            source_module: 'smart_task',
            activity_tags: ['smart_task'],
            parent_work_item_id: resolvedWorkItemId,
            vehicle_id: isVehicleLinkedExpenseType(selectedEntryType) ? (input.linkToVehicle ? input.vehicleId : null) : null,
            vehicle_registration: isVehicleLinkedExpenseType(selectedEntryType) ? (input.linkToVehicle ? input.vehicleRegistration : null) : null,
            entry_type: selectedEntryType,
            base_currency: baseCurrency,
            is_foreign_currency: detectedCurrency !== baseCurrency,
            is_billable: isJobLinked,
            // Include job linkage in custom_fields so downstream consumers
            // (BillingDashboard, JobCostingDashboard) can find the link.
            linked_job_id: linkedJobId || undefined,
            linked_customer_id: linkedCustomerId || undefined,
            linked_customer_name: linkedCustomerName || undefined,
            allocation_type: allocationType,
            extraction_provenance: receiptResult ? {
              source: receiptResult.extraction_source ?? null,
              model: receiptResult.extraction_model ?? null,
              confidence: receiptResult.confidence ?? null,
              page_count: receiptResult.page_count ?? null,
              extracted_at: new Date().toISOString(),
            } : null,
          },
        };

        if (isProcurementEntry) {
          intakePayload.purchase_order_id = input.selectedPurchaseOrder?.id || undefined;
          intakePayload.contact_id = input.selectedSupplier ? input.selectedSupplier.id : undefined;
          intakePayload.invoice_number = selectedEntryType === 'delivery_note' || selectedEntryType === 'supplier_quote'
            ? undefined
            : formDataRecord.invoice_number;
          if (selectedEntryType === 'supplier_quote') {
            intakePayload.quote_number = formDataRecord.quote_number || undefined;
          }
          intakePayload.is_partial_delivery = selectedEntryType === 'delivery_note' && input.selectedPurchaseOrder
            ? (() => {
              const totalReceived = intakeLineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
              const totalOrdered = (input.poLineItems ?? []).reduce((sum: number, item: { quantity?: number }) => sum + (item.quantity || 0), 0);
              return totalOrdered > 0 && totalReceived < totalOrdered;
            })()
            : false;
        } else if (input.selectedSupplier) {
          intakePayload.contact_id = input.selectedSupplier.id;
        }

        const res = await fetch('/api/v1/finance/expense-intake', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(intakePayload),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          if (res.status === 409 && errBody?.error === 'Duplicate document detected') {
            notify.warning('This slip has already been submitted. Duplicate document detected.');
          } else {
            const errText = errBody?.error || await res.text().catch(() => 'Unknown error');
            logger.error('[useCaptureSession] Failed to submit to expense intake:', errText);
            notify.error('Failed to submit expense for processing');
          }
          return;
        }

        const data = await res.json();
        if (data?.success) {
          const intakeId = data.data?.id;
          const itemNumber = data.data?.item_number;

          // No success toast here — the submission receipt modal already
          // confirms the submission and shows the DIN.
          input.onSubmit({
            type: selectedEntryType,
            work_item_id: resolvedWorkItemId,
            timestamp: new Date().toISOString(),
            location: input.gpsLocation || undefined,
            amount,
            notes,
            photos,
            metadata: {
              ...metadata,
              expense_intake_id: intakeId,
              item_number: itemNumber,
            },
            already_persisted: true,
          });
          return;
        }

        // API returned HTTP 200 but { success: false } — do NOT fall through
        // to the non-expense path, which would call onSubmit without
        // already_persisted and cause the parent to re-persist a duplicate.
        notify.error(data?.error || 'Failed to submit expense for processing');
        return;
      }

      // Fallback for non-expense types
      const resolvedWorkItemId = input.linkToWorkItem && input.linkableWorkItemId
        ? input.linkableWorkItemId
        : input.selectedJob
          ? input.selectedJob.id
          : input.workItemId;
      input.onSubmit({
        type: selectedEntryType,
        work_item_id: resolvedWorkItemId,
        timestamp: new Date().toISOString(),
        location: input.gpsLocation || undefined,
        amount,
        notes,
        photos,
        metadata,
      });
    } catch (err) {
      if (!isSilentFetchError(err)) logger.error('[useCaptureSession] Submit error:', err);
      notify.error('Failed to submit expense');
    } finally {
      setSubmitting(false);
      setLoadingPhase(null);
      submittingRef.current = false;
    }
  }, [formData, formDataRecord, receiptPhotos, receiptResult, detectedCurrency, baseCurrency, publicUploadToken]);

  const hasRequiredFields = Boolean(formDataRecord.amount && formDataRecord.amount.trim() !== '');
  const canSubmit = !submitting && hasRequiredFields;

  return {
    formData,
    formDataRecord,
    setFormData,
    setFormField,
    resetForm,
    resetSessionId,
    receiptPhotos,
    setReceiptPhotos,
    receiptResult,
    setReceiptResult,
    receiptValidation,
    setReceiptValidation,
    detectedCurrency,
    setDetectedCurrency,
    baseCurrency,
    submitting,
    loadingPhase,
    submit,
    canSubmit,
  };
}
